
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, KeyRound, ArrowRight, ShieldAlert, Cpu, Fingerprint } from 'lucide-react';
import { playSound } from '../services/audioService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  targetPin: string;
}

export const StaffPinModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, targetPin }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setPin('');
      setError(false);
    }
  }, [isOpen]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (pin === targetPin) {
      playSound('success');
      onSuccess();
      onClose();
    } else {
      setError(true);
      setPin('');
      playSound('error');
      setTimeout(() => setError(false), 500);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-sal-gunmetal/95 backdrop-blur-md"
        />

        <motion.div 
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ 
            scale: 1, 
            opacity: 1, 
            y: 0,
            x: error ? [0, -10, 10, -10, 10, 0] : 0
          }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative bg-sal-gunmetal-light/80 w-full max-w-sm rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.8)] overflow-hidden border border-white/10 backdrop-blur-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-sal-cyber-teal" />
          
          <div className="p-10 flex flex-col items-center text-center">
            <div className={`w-20 h-20 rounded-3xl mb-6 flex items-center justify-center transition-all duration-500 shadow-lg ${error ? 'bg-rose-600/20 text-rose-500 border border-rose-500/30 animate-shake' : 'bg-sal-cyber-teal/10 text-sal-cyber-teal border border-sal-cyber-teal/30 group-hover:scale-105'}`}>
              {error ? <ShieldAlert className="w-10 h-10" /> : <Fingerprint className="w-10 h-10" />}
            </div>
            
            <h3 className="text-3xl font-black text-white uppercase tracking-tighter mb-1">Acesso ao Campo</h3>
            <p className="text-slate-500 text-xs font-black uppercase tracking-[0.3em] mb-10">Piloto v3.4 Security Layer</p>

            <form onSubmit={handleSubmit} className="w-full space-y-8">
              <div className="relative group">
                <div className="absolute inset-0 bg-sal-cyber-teal/5 rounded-2xl blur-xl transition-opacity group-focus-within:opacity-100 opacity-0" />
                <input 
                  type="password" 
                  inputMode="numeric"
                  value={pin}
                  onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                  className={`relative w-full bg-black/40 border-2 text-center text-4xl font-mono tracking-[0.5em] py-5 rounded-2xl focus:outline-none transition-all text-white placeholder-slate-900 ${error ? 'border-rose-600 shadow-[0_0_30px_rgba(225,29,72,0.2)]' : 'border-white/5 focus:border-sal-cyber-teal focus:shadow-[0_0_30px_rgba(6,182,212,0.2)]'}`}
                  placeholder="••••"
                  maxLength={4}
                  autoFocus
                />
              </div>

              <div className="flex gap-4">
                <button 
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-5 rounded-2xl font-black text-xs uppercase tracking-[0.2em] text-slate-600 hover:text-white hover:bg-white/5 transition-all"
                >
                  Abortar
                </button>
                <button 
                  type="submit"
                  disabled={pin.length < 4}
                  className="flex-[2] bg-sal-cyber-teal text-sal-gunmetal font-black py-5 rounded-2xl shadow-xl shadow-cyan-900/30 hover:brightness-110 active:scale-[0.98] transition-all uppercase tracking-[0.2em] text-sm flex items-center justify-center gap-3 disabled:opacity-20 disabled:grayscale"
                >
                  Desbloquear <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
            
            <div className="mt-8 flex items-center gap-2 px-3 py-1.5 bg-black/20 rounded-full border border-white/5">
                <Cpu className="w-3 h-3 text-slate-700" />
                <span className="text-xs text-slate-600 font-black uppercase tracking-widest"> Sofia Encryption Active</span>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
